import { useState, useEffect, useCallback } from "react";
import AddProduct from "../components/product/AddProduct";
import EditProduct from "../components/product/EditProduct";
import Modal from "../components/Modal";
import api from "../utils/api";
import { useNavigate } from "react-router-dom";
import AddBtn from "../components/AddBtn";
import EditBtn from "../components/EditBtn";
import DeleteBtn from "../components/DeleteBtn";
import RoleAccess from "../components/RoleAccess";

export default function ProductList() {
  const [isModalOpenForAdd, setIsModalOpenForAdd] = useState(false);
  const [isModalOpenForEdit, setIsModalOpenForEdit] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const itemsPerPage = 5;
  const navigate = useNavigate();

  const fetchUserProducts = useCallback(async (page, q) => {
    setLoading(true);
    try {
      const res = await api.get(`/users/me/products`, {
        params: { page, limit: itemsPerPage, q },
        withCredentials: true,
      });

      setProducts(res.data.products || []);
      if (res.data.pagination)
        setTotalPages(res.data.pagination.totalPages || 1);
    } catch (err) {
      console.error("Axios error:", err);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api
      .get("/users/me", { withCredentials: true })
      .then((res) => setUserRole(res.data.role))
      .catch(() => setUserRole(null));
  }, []);

  useEffect(() => {
    fetchUserProducts(currentPage, search);
  }, [currentPage, search, fetchUserProducts]);

  const deleteProduct = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?"))
      return;

    try {
      await api.delete(`/products/${id}`, { withCredentials: true });
      fetchUserProducts(currentPage, search);
    } catch (err) {
      console.error(err);
      alert("Failed to delete product!");
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearch(searchInput);
  };

  const handleClear = () => {
    setSearchInput("");
    setCurrentPage(1);
    setSearch("");
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <>
      <Modal
        isOpen={isModalOpenForAdd}
        onClose={() => setIsModalOpenForAdd(false)}
      >
        <AddProduct
          onSuccess={async () => {
            setIsModalOpenForAdd(false);
            setCurrentPage(1);
            fetchUserProducts(1, search);
          }}
        />
      </Modal>

      <Modal
        isOpen={isModalOpenForEdit}
        onClose={() => setIsModalOpenForEdit(false)}
      >
        {editingProduct && (
          <EditProduct
            product={editingProduct}
            onSuccess={async () => {
              setIsModalOpenForEdit(false);
              fetchUserProducts(currentPage, search);
            }}
          />
        )}
      </Modal>

      <div>
        <h1 className="text-2xl font-bold mt-10 ml-2">Product List</h1>

        <div className="ml-2 mt-5 bg-white rounded-md shadow-lg p-5">
          <RoleAccess allow={["Admin", "Manager"]} role={userRole}>
            <div className="mb-4">
              <AddBtn
                onClick={() => setIsModalOpenForAdd(true)}
                label="Add Product"
              />
            </div>
          </RoleAccess>

          <div className="mb-4 flex justify-end gap-2">
            <input
              type="text"
              placeholder="Search by name, product code, or description..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-[450px] px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition"
            >
              Search
            </button>
            <button
              onClick={handleClear}
              className="px-4 py-2 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition"
            >
              Clear
            </button>
          </div>

          <table className="w-full text-sm border-separate border-spacing-y-2">
            <thead>
              <tr className="bg-slate-100 text-slate-600">
                <th className="px-4 py-3 text-left rounded-tl-xl rounded-bl-xl w-1/6">
                  Name
                </th>
                <th className="px-4 py-3 text-center">Product Code</th>
                <th className="px-4 py-3 text-center">Description</th>
                {userRole && ["Admin", "Manager"].includes(userRole) ? (
                  <>
                    <th className="px-4 py-3 text-center">Status</th>
                    <th className="px-4 py-3 text-right rounded-tr-xl rounded-br-xl">
                      Actions
                    </th>
                  </>
                ) : (
                  <th className="px-4 py-3 text-center rounded-tr-xl rounded-br-xl">
                    Status
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    Loading products...
                  </td>
                </tr>
              ) : products.length ? (
                products.map((p) => (
                  <tr
                    key={p._id}
                    className="border-t cursor-pointer transition hover:bg-slate-100"
                    onClick={() =>
                      navigate(`/dashboard/products/${p._id}/content-owners`)
                    }
                  >
                    <td className="px-4 py-3 flex items-center gap-x-2">
                      {p.imageUrl && (
                        <img
                          src={`${process.env.REACT_APP_API_URL}/${p.imageUrl}`}
                          a
                          alt={p.name}
                          className="w-10 h-10 object-cover rounded-xl"
                        />
                      )}
                      {p.name}
                    </td>
                    <td className="px-4 py-3 text-center">{p.productCode}</td>
                    <td className="px-4 py-3 text-center">{p.description}</td>
                    <td className="px-4 py-4 text-center">
                      <span
                        className={`inline-flex items-center justify-center rounded-full px-3 py-2 text-xs font-medium text-white ${
                          p.status === "Active" ? "bg-green-500" : "bg-red-600"
                        }`}
                      >
                        {p.status}
                      </span>
                    </td>
                    {userRole && ["Admin", "Manager"].includes(userRole) && (
                      <td className="px-4 py-6 flex justify-end gap-1">
                        <div className="flex items-center gap-4">
                          <EditBtn
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingProduct(p);
                              setIsModalOpenForEdit(true);
                            }}
                            label="Edit Product"
                          />
                          <DeleteBtn
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteProduct(p._id);
                            }}
                            label="Delete product"
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    No products found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-1 mt-4">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Prev
              </button>

              <span className="px-3 py-1 font-medium">
                {currentPage} / {totalPages}
              </span>

              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
